import _ from 'lodash';
import React, { useState } from 'react';
import { Text, Divider, Icon, Flex, Box, Button, Label } from '@vibe/core';
import { type BoardSource, type BoardSourceFieldValue, type PortalSettings, BoardSourceSchema } from '@gorilla/portal-shared/src/schemas';
import { Subitems, Board, Item, MoveArrowRight, Person, Team, BoardPrivate } from '@vibe/icons';
import { BoardSourcePickerModal } from './BoardSourcePickerModal';
import {
  type BoardSourceDescriptorType,
  boardSourceDescriptors,
  boardSourceDescriptorsByType,
  BoardSourceIndicator,
} from './board-sources/index';

function createRandomId() {
  return btoa(Math.random().toString()).substring(5, 15);
}

type BoardSourceTypeSelectionStepProps = {
  portalSettings: PortalSettings;
  selectedBoardSourceType: BoardSourceDescriptorType;
  onChangeSelectedBoardSourceType: (value: BoardSourceDescriptorType) => void;
  onSelectBoardSource: (value: BoardSourceFieldValue) => void;
  onContinue: () => void;
};

function BoardSourceTypeSelectionStep({
  portalSettings,
  selectedBoardSourceType,
  onChangeSelectedBoardSourceType,
  onSelectBoardSource,
  onContinue,
}: BoardSourceTypeSelectionStepProps) {
  const [showBoardSourcePickerModal, setShowBoardSourcePickerModal] = useState(false);

  return (
    <div className="section__field section__field--boardsourcewizard">
      {showBoardSourcePickerModal ? (
        <BoardSourcePickerModal
          portalSettings={portalSettings}
          onClose={(selectedBoardSource) => {
            setShowBoardSourcePickerModal(false);
            if (selectedBoardSource) {
              onSelectBoardSource({
                boardSourceId: selectedBoardSource.id,
              });
            }
          }}
        />
      ) : null}
      <Box>
        <Box rounded="small" border={true} borderColor="uiBorderColor">
          <Flex
            direction="column"
            justify="center"
            align="center"
            gap={8}
            style={{
              margin: '0 auto',
              width: '100%',
            }}
          >
            <Flex direction="column" align="stretch" gap={0} style={{ width: '100%' }}>
              {boardSourceDescriptors.map((boardSourceDescriptor, index) => {
                return (
                  <React.Fragment key={boardSourceDescriptor.type}>
                    <div
                      style={{
                        padding: '0 28px 0 16px',
                      }}
                      className={
                        boardSourceDescriptor.type === selectedBoardSourceType
                          ? 'boardsource__item boardsource__item--selected'
                          : 'boardsource__item'
                      }
                      onClick={() => {
                        onChangeSelectedBoardSourceType(boardSourceDescriptor.type);
                      }}
                    >
                      <Flex
                        direction="row"
                        justify="start"
                        align="center"
                        gap={32}
                        style={{
                          pointerEvents: 'none',
                          marginLeft: '8px',
                          height: '86px',
                        }}
                      >
                        <div style={{ flex: 0 }}>
                          {selectedBoardSourceType === boardSourceDescriptor.type ? (
                            <div
                              style={{
                                width: '8px',
                                height: '8px',
                                fontSize: 0,
                                lineHeight: 0,
                                borderRadius: '100%',
                                border: '5px solid #0073ea',
                              }}
                            >
                              &nbsp;
                            </div>
                          ) : (
                            <div
                              className="boardsource__item--radiobutton"
                              style={{
                                width: '14px',
                                height: '14px',
                                fontSize: 0,
                                lineHeight: 0,
                                borderRadius: '100%',
                                border: '2px solid #D0D4E4',
                              }}
                            >
                              &nbsp;
                            </div>
                          )}
                        </div>
                        <Flex
                          direction="column"
                          justify="start"
                          align="start"
                          gap={4}
                          style={{
                            flexShrink: 1,
                            marginLeft: '-8px',
                            maxWidth: 'calc(100% - 160px)',
                            color: selectedBoardSourceType === boardSourceDescriptor.type ? '#0073ea' : '#323338',
                          }}
                        >
                          <Flex gap={8} style={{ maxWidth: '100%' }}>
                            <Text
                              type="text1"
                              align="start"
                              color="inherit"
                              weight="medium"
                              element="div"
                              ellipsis
                              style={{ maxWidth: '100%' }}
                            >
                              {boardSourceDescriptor.description}
                            </Text>
                            {boardSourceDescriptor.tag && (
                              <Label
                                text={boardSourceDescriptor.tag}
                                size="medium"
                                color={selectedBoardSourceType === boardSourceDescriptor.type ? 'primary' : 'dark'}
                              />
                            )}
                          </Flex>
                          <Text type="text2" align="start" color="secondary" element="div" ellipsis style={{ maxWidth: '100%' }}>
                            {boardSourceDescriptor.example}
                          </Text>
                        </Flex>
                        <Box
                          style={{
                            flex: 0,
                            marginLeft: 'auto',
                            overflow: 'visible',
                            position: 'relative',
                          }}
                        >
                          <Flex gap={2}>
                            <div
                              style={{
                                width: '28px',
                                height: '24px',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                borderRadius: '4px',
                                lineHeight: '0',
                                color: '#323338',
                              }}
                            >
                              {boardSourceDescriptor.type === 'subitems' && (
                                <div
                                  style={{
                                    transform: 'translate3d(0,-1px,0)',
                                  }}
                                >
                                  <svg width="29" height="29" viewBox="0 0 18 18" fill="#323338" xmlns="http://www.w3.org/2000/svg">
                                    <path
                                      fillRule="evenodd"
                                      clipRule="evenodd"
                                      d="M6.74687 4.0502H14.3969C14.6454 4.0502 14.8469 4.25167 14.8469 4.5002L14.9194 7C15.3983 7.07779 15.799 7.4965 16.1969 7.74296V4.5002C16.1969 3.50608 15.391 2.7002 14.3969 2.7002H3.59687C2.60276 2.7002 1.79688 3.50608 1.79688 4.5002V13.5002C1.79688 14.4943 2.60276 15.3002 3.59687 15.3002H8.44597L9.44597 13.9502H6.74687V4.0502ZM5.39687 4.0502H3.59687C3.34835 4.0502 3.14687 4.25167 3.14687 4.5002V13.5002C3.14687 13.7487 3.34835 13.9502 3.59687 13.9502H5.39687V4.0502Z"
                                    />
                                    <path
                                      fillRule="evenodd"
                                      clipRule="evenodd"
                                      d="M13.5003 8C12.8169 8 12.1615 8.2553 11.6782 8.70974C11.195 9.16418 10.9235 9.78053 10.9235 10.4232C10.9235 11.0659 11.195 11.6822 11.6782 12.1367C12.1615 12.5911 12.8169 12.8464 13.5003 12.8464C14.1838 12.8464 14.8392 12.5911 15.3224 12.1367C15.8057 11.6822 16.0772 11.0659 16.0772 10.4232C16.0772 9.78053 15.8057 9.16418 15.3224 8.70974C14.8392 8.2553 14.1838 8 13.5003 8ZM13.227 13.4426H13.7764C14.5202 13.4689 15.2475 13.6562 15.9018 13.99C16.5276 14.3068 17.057 14.7686 17.44 15.3317C17.8073 15.8809 18 16.4997 18 17.1285V17.5599C18 17.8031 17.764 18 17.472 18H9.52804C9.23602 18 9 17.8031 9 17.5599V17.1285C9 16.4997 9.19268 15.8809 9.56004 15.3317C9.94318 14.7686 10.4728 14.3068 11.0988 13.99C11.7537 13.6559 12.4825 13.4686 13.227 13.4426Z"
                                    />
                                  </svg>
                                </div>
                              )}
                              {boardSourceDescriptor.type === 'client-board' && <Icon icon={BoardPrivate} size={30} />}
                              {boardSourceDescriptor.type === 'shared-client-board' && <Icon icon={Item} size={30} />}
                              {boardSourceDescriptor.type === 'board' && <Icon icon={Board} size={30} />}
                            </div>
                            <div
                              style={{
                                width: '28px',
                                height: '24px',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                borderRadius: '4px',
                                lineHeight: '0',
                                color: '#323338',
                              }}
                              className="boardsource__icon"
                            >
                              <Icon icon={MoveArrowRight} size={18} />
                            </div>
                            <div
                              style={{
                                width: '28px',
                                height: '24px',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                borderRadius: '4px',
                                lineHeight: '0',
                                color: '#323338',
                              }}
                              className="boardsource__icon"
                            >
                              {boardSourceDescriptor.type === 'subitems' && <Icon icon={Subitems} size={30} />}
                              {boardSourceDescriptor.type === 'client-board' && <Icon icon={Person} size={26} />}
                              {boardSourceDescriptor.type === 'shared-client-board' && <Icon icon={Person} size={26} />}
                              {boardSourceDescriptor.type === 'board' && <Icon icon={Team} size={27} />}
                            </div>
                          </Flex>
                        </Box>
                      </Flex>
                    </div>
                    {index !== boardSourceDescriptors.length - 1 && <Divider withoutMargin />}
                  </React.Fragment>
                );
              })}
            </Flex>
          </Flex>
        </Box>
      </Box>
      <Box marginTop="large">
        <Flex gap={32}>
          <Box
            style={{
              flex: 1,
            }}
          >
            <Flex gap={24}>
              {portalSettings.boardSources && portalSettings.boardSources.length > 0 ? (
                <Text type="text2" align="start" color="primary" element="p">
                  Use an existing board setup? Find and link it{' '}
                  <a
                    href="#"
                    target="_blank"
                    onClick={(e) => {
                      e.preventDefault();
                      setShowBoardSourcePickerModal(true);
                    }}
                    style={{ textDecoration: 'underline' }}
                  >
                    here
                  </a>
                  .
                </Text>
              ) : (
                <Text type="text2" align="start" color="primary" element="p">
                  Not sure what to use? Read the{' '}
                  <a href="https://getgorilla.app/products/client-portal/docs/user-guides/board-setup" target="_blank">
                    documentation
                  </a>{' '}
                  to learn more.
                </Text>
              )}
            </Flex>
          </Box>
          <Box marginEnd="auto">
            <Flex gap={10}>
              <Button
                kind="primary"
                size="medium"
                onClick={() => {
                  onContinue();
                }}
              >
                Next
              </Button>
            </Flex>
          </Box>
        </Flex>
      </Box>
    </div>
  );
}
type BoardSourceExplainerStepProps = {
  selectedBoardSourceType: BoardSourceDescriptorType;
  onContinue: () => void;
  onBack: () => void;
};

function BoardSourceExplainerStep({ onContinue, onBack, selectedBoardSourceType }: BoardSourceExplainerStepProps) {
  const boardSourceDescriptor = boardSourceDescriptorsByType[selectedBoardSourceType];
  const BoardSourceExplainerComponent = boardSourceDescriptor.explainerComponent;

  return (
    <Box className="section__field" style={{ overflow: 'visible' }}>
      <BoardSourceExplainerComponent />
      <Flex gap={16}>
        <Box
          style={{
            flex: 1,
          }}
        >
          <Flex gap={24} style={{ width: '100%' }}>
            <Text type="text2" align="start" color="primary" ellipsis element="div">
              <BoardSourceIndicator boardSourceType={boardSourceDescriptor.type}>{boardSourceDescriptor.description}</BoardSourceIndicator>
            </Text>
          </Flex>
        </Box>
        <Box marginEnd="auto">
          <Flex gap={10}>
            <Button
              kind="tertiary"
              size="medium"
              onClick={() => {
                onBack();
              }}
            >
              Back
            </Button>
            <Button
              kind="primary"
              size="medium"
              onClick={() => {
                onContinue();
              }}
            >
              Next
            </Button>
          </Flex>
        </Box>
      </Flex>
    </Box>
  );
}

type BoardSourceConfigurationStepProps = {
  selectedBoardSourceType: BoardSourceDescriptorType;
  boardSourceDraft: Partial<BoardSource>;
  onChangeBoardSourceDraft: (source: Partial<BoardSource>) => void;
  onContinue: () => void;
  onBack: () => void;
};

function BoardSourceConfigurationStep({
  onContinue,
  onBack,
  onChangeBoardSourceDraft,
  selectedBoardSourceType,
  boardSourceDraft,
}: BoardSourceConfigurationStepProps) {
  const boardSourceDescriptor = boardSourceDescriptorsByType[selectedBoardSourceType];
  const boardSourceDraftResult = BoardSourceSchema.safeParse(boardSourceDraft);
  const BoardSourceComponent = boardSourceDescriptor.component;

  if (!BoardSourceComponent) {
    return null;
  }

  return (
    <Box className="section__field">
      <BoardSourceComponent
        // @ts-ignore
        source={boardSourceDraft}
        onChange={(source) => {
          onChangeBoardSourceDraft(source);
        }}
        mode="create"
      />
      <Flex gap={16}>
        <Box
          style={{
            flex: 1,
          }}
        >
          <Flex gap={24}>
            <Text type="text2" align="start" color="primary" ellipsis element="p">
              <BoardSourceIndicator boardSourceType={boardSourceDescriptor.type}>{boardSourceDescriptor.description}</BoardSourceIndicator>
            </Text>
          </Flex>
        </Box>
        <Box marginEnd="auto">
          <Flex gap={10}>
            <Button
              kind="tertiary"
              size="medium"
              onClick={() => {
                onBack();
              }}
            >
              Back
            </Button>
            <Button
              disabled={!boardSourceDraftResult.success || !boardSourceDraft?.name}
              kind="primary"
              size="medium"
              onClick={() => {
                onContinue();
              }}
            >
              Next
            </Button>
          </Flex>
        </Box>
      </Flex>
    </Box>
  );
}

type Step = 'selection' | 'explainer' | 'configuration';

type BoardSourceSetupWizardProps = {
  portalSettings: PortalSettings;
  onDone: (boardSourceValue: BoardSourceFieldValue, newBoardSource?: BoardSource) => void;
};

export function BoardSourceSetupWizard({ portalSettings, onDone }: BoardSourceSetupWizardProps) {
  const [step, setStep] = useState<Step>('selection');
  const [selectedBoardSourceType, setSelectedBoardSourceType] = useState<BoardSourceDescriptorType>('subitems');
  const [boardSourceDraft, setBoardSourceDraft] = useState<Partial<BoardSource> | null>();

  if (step === 'selection') {
    return (
      <BoardSourceTypeSelectionStep
        portalSettings={portalSettings}
        selectedBoardSourceType={selectedBoardSourceType}
        onChangeSelectedBoardSourceType={(value) => {
          setSelectedBoardSourceType(value);

          if (value === 'subitems') {
            setBoardSourceDraft(null);
          } else {
            const defaults = _.cloneDeep(boardSourceDescriptorsByType[value].defaultValues);
            setBoardSourceDraft({ ...defaults, id: createRandomId() } as BoardSource);
          }
        }}
        onSelectBoardSource={(value) => {
          onDone(value);
        }}
        onContinue={() => {
          setStep('explainer');
        }}
      />
    );
  }

  if (step === 'explainer') {
    return (
      <BoardSourceExplainerStep
        selectedBoardSourceType={selectedBoardSourceType}
        onBack={() => {
          setStep('selection');
        }}
        onContinue={() => {
          if (selectedBoardSourceType === 'subitems') {
            onDone({
              type: 'subitems',
            });
            return;
          } else {
            setStep('configuration');
          }
        }}
      />
    );
  }

  if (step === 'configuration' && boardSourceDraft) {
    return (
      <BoardSourceConfigurationStep
        selectedBoardSourceType={selectedBoardSourceType}
        boardSourceDraft={boardSourceDraft}
        onChangeBoardSourceDraft={(source) => {
          setBoardSourceDraft(source);
        }}
        onBack={() => {
          setStep('explainer');
        }}
        onContinue={() => {
          onDone(
            {
              boardSourceId: boardSourceDraft.id,
            } as BoardSourceFieldValue,
            boardSourceDraft as BoardSource,
          );
        }}
      />
    );
  }

  return <div>Not implemented</div>;
}
